/* Dependencies */
import { TrapFocusOptions } from './TrapFocus.model';

/**
 * Creates the functionality to handle trapping a users focus within a desired element.
 */
export class TrapFocus {
  private focusableElements: HTMLElement[];
  private container: HTMLElement | DocumentFragment;
  private options: TrapFocusOptions = {
    escapeClose: true,
  };
  private isActive = false;
  private originalFocusElement: HTMLElement;

  /**
   * Getter for private active state.
   */
  get activeState(): boolean {
    return this.isActive;
  }

  /**
   * Getter for private original element.
   */
  get originalElement(): HTMLElement | null {
    return this.originalFocusElement;
  }

  /**
   * Setter for private original element.
   */
  set originalElement(value) {
    this.originalFocusElement = value;
  }

  /**
   * Constructor
   * @param container - The container element.
   */
  constructor(
    container: HTMLElement | DocumentFragment,
    options?: TrapFocusOptions
  ) {
    this.container = container;

    if (options) {
      this.options = { ...this.options, ...options };
    }
  }

  /**
   * Selects and stores all focusable elements within the container.
   */
  public defineFocusableElements(): void {
    this.focusableElements = Array.prototype.slice.call(
      this.container.querySelectorAll(
        `a[href]:not([disabled]), 
         button:not([disabled]), 
         textarea:not([disabled]), 
         input[type="text"]:not([disabled]), 
         input[type="radio"]:not([disabled]), 
         input[type="checkbox"]:not([disabled]), 
         input[type="file"]:not([disabled]), 
         select:not([disabled]),
         iframe`
      )
    );
  }

  /**
   * Applies browser focus to the first available element.
   */
  public focusFirstElement(): void {
    if (this.focusableElements.length === 0) {
      return;
    }
    this.focusableElements[0].focus();
  }

  /**
   * Validate the event to avoid mouse based trapping.
   * @param event - The event to validate.
   */
  public checkKeyboardEvent(event: Event): boolean {
    // Using https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent/pointerType
    // To detect pointerType and fire function if not "mouse", "touch" or "pen".
    const events = ['mouse', 'touch', 'pen'];
    // @ts-ignore
    return !events.includes(event.pointerType);
  }

  /**
   * Attaches the trap focus handling.
   */
  public attachTrapFocus(): void {
    this.defineFocusableElements();
    this.container.addEventListener('keydown', this.handleTrapFocus.bind(this));

    if (this.options.escapeClose) {
      this.originalFocusElement = document.activeElement as HTMLElement;
    }
    this.isActive = true;
    this.focusFirstElement();
  }

  /**
   * Removes the trap fous handling.
   */
  public dettachTrapFocus(): void {
    this.container.removeEventListener(
      'keydown',
      this.handleTrapFocus.bind(this)
    );

    if (this.options.escapeClose && this.originalFocusElement) {
      this.originalFocusElement.focus();
    }
    this.originalFocusElement = null;
    this.isActive = false;
  }

  /**
   * Handles the user focus events and ensures the user cannot tab outwidth the container.
   * @param event - The Keyboard event thats fired.
   */
  private handleTrapFocus(event: KeyboardEvent): void {
    // Is Active flight check to prevent collision between close and escape events.
    if (!this.isActive) {
      return;
    }

    const isTab = event.key === 'Tab' || event.keyCode === 9;
    const isEscape = event.key === 'Escape' || event.keyCode === 27;
    const currentFocusIndex = this.focusableElements.findIndex(
      (element) => element === event.target
    );

    // Handle the escape to close event
    if (this.options.escapeClose && isEscape) {
      // Stop the event from bubbling.
      event.stopPropagation();

      // Handle ignoring elements
      if (
        this.options.escapeIgnoreElements &&
        this.options.escapeIgnoreElements.length
      ) {
        const target = event.target as HTMLElement;
        const shouldIgnore = this.options.escapeIgnoreElements.find(
          (elementId) => {
            return target.closest(elementId) !== null;
          }
        );

        // Ignore if contained within the element specified
        if (shouldIgnore) {
          return;
        }
      }

      this.dettachTrapFocus();
      if (this.options.escapeCloseCB) {
        this.options.escapeCloseCB();
      }
      return;
    }

    // Handle Keyboard movement
    if (this.options.handleKeyMovement) {
      // Prevent default if required.
      const preventDefault = ['ArrowUp', 'ArrowDown', 'Home', 'End'];

      if (preventDefault.includes(event.code)) {
        event.preventDefault();
      }

      // Ignore If within an iframe
      const target = event.target as HTMLElement;
      if (target.closest('iframe')) {
        return;
      }

      switch (event.code) {
        case 'ArrowUp': {
          if (currentFocusIndex < 1) {
            this.focusableElements[this.focusableElements.length - 1].focus();
            return;
          }
          this.focusableElements[currentFocusIndex - 1].focus();
          return;
        }
        case 'ArrowDown': {
          if (currentFocusIndex >= this.focusableElements.length - 1) {
            this.focusableElements[0].focus();
            return;
          }
          this.focusableElements[currentFocusIndex + 1].focus();
          return;
        }
        case 'Home': {
          this.focusableElements[0].focus();
          return;
        }
        case 'End': {
          this.focusableElements[this.focusableElements.length - 1].focus();
          return;
        }
      }
    }

    if (!isTab || this.focusableElements.length === 0) {
      return;
    }

    const activeElement = document.activeElement;

    if (event.shiftKey && activeElement === this.focusableElements[0]) {
      this.focusableElements[this.focusableElements.length - 1].focus();
      event.preventDefault();
      return;
    }

    if (
      !event.shiftKey &&
      activeElement ===
        this.focusableElements[this.focusableElements.length - 1]
    ) {
      this.focusableElements[0].focus();
      event.preventDefault();
      return;
    }
  }
}
